import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { debug } from '@/utils/debug';

interface FocusModeContextType {
  isFocusMode: boolean;
  toggleFocusMode: () => void;
}

const FocusModeContext = createContext<FocusModeContextType | undefined>(undefined);

export const FocusModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isFocusMode, setIsFocusMode] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('app-focus-mode');
      return saved ? JSON.parse(saved) : false;
    } catch (error) {
      debug.error('Error parsing focus mode from localStorage:', error);
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('app-focus-mode', JSON.stringify(isFocusMode));
    } catch (error) {
      debug.error('Error saving focus mode to localStorage:', error);
    }
  }, [isFocusMode]);

  const toggleFocusMode = useCallback(() => {
    setIsFocusMode((prev) => !prev);
  }, []);

  const contextValue = useMemo(
    () => ({
      isFocusMode,
      toggleFocusMode,
    }),
    [isFocusMode, toggleFocusMode]
  );

  return <FocusModeContext.Provider value={contextValue}>{children}</FocusModeContext.Provider>;
};

export const useFocusMode = () => {
  const context = useContext(FocusModeContext);
  if (context === undefined) {
    throw new Error('useFocusMode must be used within a FocusModeProvider');
  }
  return context;
};
